import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  LayoutDashboard,
  FileText,
  ArrowRight,
} from 'lucide-react';

import PortcoEmbeddedPanel from './PortcoEmbeddedPanel';
import GPDashboardScreen from '../portco/GPDashboardScreen';
import PortcoDetailScreen from '../portco/PortcoDetailScreen';
import { PortcoEmbeddedDark } from '../portco/PortcoTheme';

/**
 * Portfolio Companies Workbench — Path B cut #1, Tasks #8 + #9.
 *
 * Sub-nav inside Alternatives → Portfolio Companies. Replaces the standalone
 * /portco/* silo by hosting portco surfaces inside the Alternatives pillar.
 * URL deep-link: ?sub=portfolio-companies&pcs=roster|gp|detail&portcoId=N
 *
 * Pills:
 *  - Roster:        PortcoEmbeddedPanel (dark-themed); row click → Detail
 *  - GP Dashboard:  embedded GPDashboardScreen
 *  - Detail:        embedded PortcoDetailScreen, driven by ?portcoId=N
 *
 * LP Roster and FoF Look-through are NOT pills — they are Alternatives rail
 * items in their own right; hosting them here too made the same component
 * navigable from two URLs (hub audit F-HUB-017). Legacy ?pcs= deep links to
 * them fall back to Roster.
 *
 * /portco/* routes redirect into this workbench (App.tsx route shims) so
 * deep links from email, docs, and external systems don't 404.
 *
 * Covenant Map intentionally NOT a pill — covenants unify into
 * Credit & Loans → Covenants (single master) per feedback_unified_covenant_center.
 */

type PortcoPill = 'roster' | 'gp' | 'detail';

const VALID_PILLS: PortcoPill[] = ['roster', 'gp', 'detail'];

const PILLS: { id: PortcoPill; label: string; icon: React.ReactNode }[] = [
  { id: 'roster', label: 'Roster', icon: <Building2 /> },
  { id: 'gp', label: 'GP Dashboard', icon: <LayoutDashboard /> },
  { id: 'detail', label: 'Detail', icon: <FileText /> },
];

const Wrap = styled.div`
  /* Sit flush inside the Alternatives ContentWrapper — no extra padding. */
`;

const PillRow = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(15, 23, 42, 0.35);
`;

const Pill = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid
    ${(p) => (p.$active ? 'rgba(59, 130, 246, 0.55)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${(p) => (p.$active ? 'rgba(59, 130, 246, 0.16)' : 'rgba(255, 255, 255, 0.02)')};
  color: ${(p) => (p.$active ? '#bfdbfe' : '#94a3b8')};

  &:hover {
    border-color: rgba(59, 130, 246, 0.45);
    background: rgba(59, 130, 246, 0.12);
    color: #bfdbfe;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PillBody = styled.div`
  min-height: 320px;
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #94a3b8;
`;

const PlaceholderIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #93c5fd;
  margin-bottom: 1rem;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const PlaceholderTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 0.4rem;
`;

const PlaceholderSub = styled.div`
  font-size: 0.85rem;
  max-width: 460px;
  line-height: 1.5;
  margin-bottom: 1.25rem;
`;

const DeepLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(139, 92, 246, 0.4);
  background: rgba(139, 92, 246, 0.12);
  color: #c4b5fd;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.6);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PortfolioCompaniesWorkbench: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pillFromUrl = searchParams.get('pcs') as PortcoPill | null;
  const portcoIdFromUrl = searchParams.get('portcoId');
  const initialPill: PortcoPill =
    pillFromUrl && VALID_PILLS.includes(pillFromUrl) ? pillFromUrl : 'roster';

  const [activePill, setActivePill] = useState<PortcoPill>(initialPill);

  useEffect(() => {
    if (pillFromUrl && VALID_PILLS.includes(pillFromUrl) && pillFromUrl !== activePill) {
      setActivePill(pillFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pillFromUrl]);

  const handlePillChange = (next: PortcoPill) => {
    setActivePill(next);
    const params = new URLSearchParams(searchParams);
    params.set('pcs', next);
    setSearchParams(params, { replace: true });
  };

  const handleSelectPortco = (portcoId: number) => {
    setActivePill('detail');
    const params = new URLSearchParams(searchParams);
    params.set('pcs', 'detail');
    params.set('portcoId', String(portcoId));
    setSearchParams(params, { replace: true });
  };

  return (
    <Wrap>
      <PortcoEmbeddedDark />
      <PillRow>
        {PILLS.map((p) => (
          <Pill
            key={p.id}
            $active={activePill === p.id}
            onClick={() => handlePillChange(p.id)}
          >
            {p.icon}
            {p.label}
          </Pill>
        ))}
      </PillRow>

      <PillBody>
        <AnimatePresence mode="wait">
          <motion.div
            key={activePill}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
          >
            {activePill === 'roster' && (
              <PortcoEmbeddedPanel
                inWorkbench
                onSelectPortco={handleSelectPortco}
              />
            )}

            {activePill === 'gp' && (
              <div className="portco-embedded">
                <GPDashboardScreen embedded />
              </div>
            )}

            {activePill === 'detail' && (
              portcoIdFromUrl ? (
                <div className="portco-embedded">
                  <PortcoDetailScreen embedded portcoIdOverride={portcoIdFromUrl} />
                </div>
              ) : (
                <Placeholder>
                  <PlaceholderIcon>
                    <FileText />
                  </PlaceholderIcon>
                  <PlaceholderTitle>Portfolio Company Detail</PlaceholderTitle>
                  <PlaceholderSub>
                    Select a portfolio company from the Roster to view its full detail —
                    capital structure, marks history, KPIs, debt facilities, and covenant
                    status.
                  </PlaceholderSub>
                  <DeepLinkButton onClick={() => handlePillChange('roster')}>
                    Go to Roster
                    <ArrowRight />
                  </DeepLinkButton>
                </Placeholder>
              )
            )}
          </motion.div>
        </AnimatePresence>
      </PillBody>
    </Wrap>
  );
};

export default PortfolioCompaniesWorkbench;
